import { useEffect, useRef, useState } from 'react';
import useFetch from './useFetch';
import { fetchBatchImagesByBookId } from '../services/mediaService';

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_MS = 20 * 60 * 1000; // 20 minutes

/**
 * The book's pages as the server has them (the batch list), refreshed while
 * drawing is under way. Page photos and Preview both read drawing progress
 * from this, so the two steps can't disagree.
 *
 * `rows` can start as the cached copy from an earlier visit (instant render);
 * `isFresh` turns true once this mount's own fetch has returned. Count and
 * finish only on fresh rows: the cached copy can be a previous run's pages.
 *
 * While `poll` is true it refetches every 5 s. Guardrails against a runaway
 * poll (each refetch re-signs every image URL, and a single forgotten tab once
 * ran up ~300GB of egress in a weekend):
 *   1. Never poll while the tab is hidden/backgrounded.
 *   2. Hard-stop MAX_POLL_MS after the tab was last brought into view, so a
 *      forgotten tab stops, but a customer who comes back to their phone gets
 *      the pages that finished while it slept.
 *   3. Stop as soon as `isDone(rows)` says the run is over. A change of
 *      `restartKey` (the run's progress) starts it again, so a check that
 *      looked done too early can't strand the page.
 * The socket listeners are the fast path, but a phone that sleeps or switches
 * apps misses their events, so this poll must be able to finish the job alone.
 */
export default function useBookPagesFromServer(bookId, { poll = false, isDone, refreshKey, restartKey } = {}) {
  const { data, isLoading, isFetching, refetch } = useFetch({
    dependencies: ['batchImages', bookId, refreshKey],
    enabled: !!bookId,
    fetchFunction: async () => {
      if (!bookId) return null;
      const result = await fetchBatchImagesByBookId(bookId);
      return result?.docs?.data || [];
    },
  });

  // useFetch hands out the cache first and always fetches on mount: rows are
  // fresh once a fetch this mount saw start has finished.
  const [freshFor, setFreshFor] = useState(null);
  const sawFetchRef = useRef(false);
  useEffect(() => {
    sawFetchRef.current = false;
    setFreshFor(null);
  }, [bookId, refreshKey]);
  useEffect(() => {
    if (isFetching) sawFetchRef.current = true;
    else if (sawFetchRef.current) setFreshFor(bookId);
  }, [isFetching, bookId]);

  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  const isDoneRef = useRef(isDone);
  isDoneRef.current = isDone;

  useEffect(() => {
    if (!bookId || !poll) return undefined;

    let startedAt = Date.now();
    let intervalId = null;

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const tick = () => {
      if (Date.now() - startedAt > MAX_POLL_MS) {
        stopPolling();
        return;
      }
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }
      void refetchRef.current?.().then((result) => {
        const rows = result?.res;
        if (Array.isArray(rows) && isDoneRef.current?.(rows)) stopPolling();
      });
    };

    const startPolling = () => {
      if (intervalId) return;
      if (Date.now() - startedAt > MAX_POLL_MS) return;
      tick();
      intervalId = setInterval(tick, POLL_INTERVAL_MS);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startedAt = Date.now();
        startPolling();
      } else {
        stopPolling();
      }
    };

    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      startPolling();
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
      stopPolling();
    };
  }, [bookId, poll, restartKey]);

  return {
    rows: data,
    isLoading,
    isFresh: !!bookId && freshFor === bookId,
    refetch,
  };
}
